import type { LucideIcon } from "lucide-react";
import { Check, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getZoneStyle } from "@/lib/zoneStyle";

/**
 * One zone in the producer's drawing editor (festival boundary, media
 * office, crew catering). Previously each of these was a ~90-line copy
 * of the same card with a different colour hardcoded; extracting it here
 * gives a single, consistent look driven by the shared zone palette.
 *
 * The card carries a coloured icon chip in the zone's own colour, a clear
 * "set / not drawn" status, and a tidy action row. Behaviour (draw / edit
 * / delete / finish / cancel) is unchanged — the parent still owns the map
 * and passes the handlers in.
 */
export interface ZonePanelCardProps {
  areaType: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Whether this zone is optional (shown in the empty state). */
  optional?: boolean;
  /** True when a zone of this type already exists. */
  hasZone: boolean;
  /** Highlight ring when this zone is selected on the map. */
  selected?: boolean;
  /** True while this specific zone is being drawn. */
  isDrawing: boolean;
  /** True while ANY zone is being drawn (disables other draw buttons). */
  anyDrawing: boolean;
  onStartDraw: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onFinishDraw: () => void;
  onCancelDraw: () => void;
  dataGuide?: string;
}

export function ZonePanelCard({
  areaType,
  title,
  description,
  icon: Icon,
  optional = false,
  hasZone,
  selected = false,
  isDrawing,
  anyDrawing,
  onStartDraw,
  onEdit,
  onDelete,
  onFinishDraw,
  onCancelDraw,
  dataGuide,
}: ZonePanelCardProps) {
  const color = getZoneStyle(areaType).color;

  return (
    <Card
      className={`p-5 transition-shadow ${selected ? "ring-2 ring-primary" : ""}`}
      data-guide={dataGuide}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${color}22`, color }}
          aria-hidden
        >
          <Icon className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{title}</h3>
            {hasZone ? (
              <span
                className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                style={{ backgroundColor: `${color}22`, color }}
              >
                <Check className="h-3 w-3" />
                Set
              </span>
            ) : (
              <span className="ml-auto inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {optional ? "Optional" : "Not drawn"}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {isDrawing ? (
              <>
                <Button onClick={onFinishDraw} variant="default" size="sm">
                  Finish
                </Button>
                <Button onClick={onCancelDraw} variant="outline" size="sm">
                  Cancel
                </Button>
              </>
            ) : hasZone ? (
              <>
                <Button variant="outline" size="sm" onClick={onEdit} disabled={anyDrawing}>
                  <Pencil className="mr-1.5 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  disabled={anyDrawing}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
              </>
            ) : (
              <Button variant="default" size="sm" onClick={onStartDraw} disabled={anyDrawing}>
                Draw zone
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
